import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supportAPI } from '../services/api';

const STATUS_LABEL = { open: 'Open — waiting for a reply', answered: 'Answered by AWTF', closed: 'Closed' };
const STATUS_CLASS = { open: 'bg-amber-100 text-amber-700', answered: 'bg-emerald-100 text-emerald-700', closed: 'bg-slate-200 text-slate-600' };

export default function SupportTicket() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  function load() {
    return supportAPI.viewPublic(token)
      .then((r) => setData(r))
      .catch((e) => setError(e.response?.data?.error || 'This ticket could not be found.'));
  }

  useEffect(() => { load().finally(() => setLoading(false)); }, [token]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'nearest' }); }, [data?.messages?.length]);

  async function handleReply(e) {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await supportAPI.replyPublic(token, reply.trim());
      setReply('');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send.');
    } finally {
      setSending(false);
    }
  }

  return (
    <main className="min-h-[100dvh] bg-[#f4f6fa] text-slate-900">
      <header className="bg-[#1b2857] px-4 pb-6 pt-[max(16px,env(safe-area-inset-top))] text-white">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white p-1 shadow">
            <img src="/awtf_logo.png" alt="AWTF" className="h-full w-full object-contain" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[.16em] text-orange-300">Advocates Welfare Trust Fund</p>
            <h1 className="truncate text-lg font-bold">AWTF Support</h1>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-lg px-4 py-6">
        {loading && <p className="py-16 text-center text-sm text-slate-400">Loading…</p>}

        {!loading && error && !data && (
          <div className="rounded-[24px] bg-white p-7 text-center shadow-sm ring-1 ring-slate-200">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 text-2xl">⏱</div>
            <h2 className="mt-4 text-lg font-bold text-slate-800">Not found</h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">{error}</p>
          </div>
        )}

        {!loading && data && (
          <>
            <div className="flex items-center justify-between rounded-2xl bg-white px-4 py-3 shadow-sm ring-1 ring-slate-200">
              <span className="text-sm font-semibold text-slate-700">{data.full_name}</span>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_CLASS[data.status] || 'bg-slate-100 text-slate-600'}`}>
                {STATUS_LABEL[data.status] || data.status}
              </span>
            </div>

            <div className="mt-4 space-y-3">
              {data.messages.map((m, i) => (
                <div key={i} className={`flex ${m.sender_type === 'admin' ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[14px] leading-6 shadow-sm ${
                    m.sender_type === 'admin' ? 'bg-white text-slate-800 ring-1 ring-slate-200' : 'bg-orange-500 text-white'
                  }`}>
                    <p className="whitespace-pre-wrap">{m.message}</p>
                    <p className={`mt-1 text-[10px] ${m.sender_type === 'admin' ? 'text-slate-400' : 'text-orange-100'}`}>
                      {m.sender_type === 'admin' ? 'AWTF Support' : 'You'} · {m.created_at?.slice(0, 16).replace('T', ' ')}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {data.status !== 'closed' ? (
              <form onSubmit={handleReply} className="mt-4 flex gap-2">
                <input value={reply} onChange={(e) => setReply(e.target.value)}
                  className="min-w-0 flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-[16px] shadow-sm focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20 sm:text-sm"
                  placeholder="Type a message…" />
                <button type="submit" disabled={sending || !reply.trim()}
                  className="rounded-2xl bg-orange-600 px-5 py-3 text-sm font-semibold text-white shadow disabled:opacity-60">
                  {sending ? '…' : 'Send'}
                </button>
              </form>
            ) : (
              <p className="mt-4 text-center text-xs text-slate-400">This ticket is closed.</p>
            )}
            {error && <p className="mt-2 text-center text-xs text-red-600">{error}</p>}
          </>
        )}
      </div>
    </main>
  );
}
